import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import { personalActivitiesApi } from '../services/api';

const PersonalTodoList = ({ showIcon = false }) => {
  const [todos, setTodos] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTodo, setNewTodo] = useState({ title: '', description: '' });
  const [actionLoading, setActionLoading] = useState(null); // Track which item is being actioned (id or 'create')

  useEffect(() => {
    loadTodos();
  }, []);

  const loadTodos = async () => {
    try {
      const response = await personalActivitiesApi.getAll();
      setTodos(response.data);
    } catch (error) {
      console.error('Failed to load personal todos:', error);
    }
  };

  const handleCreateTodo = async () => {
    if (!newTodo.title.trim()) return;

    try {
      setActionLoading('create');
      await personalActivitiesApi.create(newTodo);
      setNewTodo({ title: '', description: '' });
      setDialogOpen(false);
      loadTodos();
    } catch (error) {
      console.error('Failed to create todo:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleComplete = async (todo) => {
    try {
      setActionLoading(`toggle-${todo.activity_id}`);
      if (todo.is_completed) {
        await personalActivitiesApi.markAsIncomplete(todo.activity_id);
      } else {
        await personalActivitiesApi.markAsComplete(todo.activity_id);
      }
      loadTodos();
    } catch (error) {
      console.error('Failed to toggle todo completion:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      setActionLoading(`delete-${todoId}`);
      await personalActivitiesApi.delete(todoId);
      loadTodos();
    } catch (error) {
      console.error('Failed to delete todo:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const incompleteTodos = todos.filter(todo => !todo.is_completed);
  const completedTodos = todos.filter(todo => todo.is_completed);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            {showIcon && <CheckBoxIcon sx={{ mr: 1 }} />}
            To-Do
          </Typography>
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
            >
              Add
            </Button>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Always show incomplete todos */}
        <List dense>
          {incompleteTodos.slice(0, expanded ? undefined : 3).map((todo) => (
            <ListItem key={todo.activity_id} sx={{ pl: 0 }}>
              <IconButton
                edge="start"
                onClick={() => handleToggleComplete(todo)}
                size="small"
                sx={{ mr: 1 }}
                disabled={actionLoading === `toggle-${todo.activity_id}`}
              >
                {actionLoading === `toggle-${todo.activity_id}` ? (
                  <CircularProgress size={20} />
                ) : (
                  <RadioButtonUncheckedIcon color="action" />
                )}
              </IconButton>
              <ListItemText
                primary={todo.title}
                secondary={todo.description}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleDeleteTodo(todo.activity_id)}
                  size="small"
                  disabled={actionLoading === `delete-${todo.activity_id}`}
                >
                  {actionLoading === `delete-${todo.activity_id}` ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        {incompleteTodos.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            No pending tasks
          </Typography>
        )}

        {/* Show completed todos only when expanded */}
        <Collapse in={expanded}>
          {completedTodos.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Completed ({completedTodos.length})
              </Typography>
              <List dense>
                {completedTodos.map((todo) => (
                  <ListItem key={todo.activity_id} sx={{ pl: 0 }}>
                    <IconButton
                      edge="start"
                      onClick={() => handleToggleComplete(todo)}
                      size="small"
                      sx={{ mr: 1 }}
                      disabled={actionLoading === `toggle-${todo.activity_id}`}
                    >
                      {actionLoading === `toggle-${todo.activity_id}` ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CheckCircleIcon color="success" />
                      )}
                    </IconButton>
                    <ListItemText
                      primary={todo.title}
                      secondary={todo.description}
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        sx: { textDecoration: 'line-through', color: 'text.secondary' }
                      }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteTodo(todo.activity_id)}
                        size="small"
                        disabled={actionLoading === `delete-${todo.activity_id}`}
                      >
                        {actionLoading === `delete-${todo.activity_id}` ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Collapse>

        {!expanded && incompleteTodos.length > 3 && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ cursor: 'pointer' }}
            onClick={() => setExpanded(true)}
          >
            +{incompleteTodos.length - 3} more...
          </Typography>
        )}

      {/* Add Todo Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Personal Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Task Title"
            fullWidth
            variant="outlined"
            value={newTodo.title}
            onChange={(e) => setNewTodo({ ...newTodo, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newTodo.description}
            onChange={(e) => setNewTodo({ ...newTodo, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={actionLoading === 'create'}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateTodo} 
            variant="contained"
            disabled={actionLoading === 'create' || !newTodo.title.trim()}
            startIcon={actionLoading === 'create' ? <CircularProgress size={16} /> : null}
          >
            {actionLoading === 'create' ? 'Adding...' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PersonalTodoList;
